import {
  ForecastAssistantInputs,
  getMinDate,
  useAssistantState,
  useLimits,
  useUpdateMutation,
} from '@e2m-customer-portal/domains';
import { Device, MarketLocation, useEnvSettings, useLocale, useStartOfWeek } from '@e2m-customer-portal/hooks';
import { InputFieldType, isBattery, isControlEnergy, Validation, validation } from '@e2m-customer-portal/shared';
import {
  DateTimePicker,
  Drawer,
  DrawerButtons,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  HourSelection,
  MasterDataItem,
  TimeSliceSelector,
  ValidatedContainer,
  WeekdaySelection,
  WeekdaySelector,
} from '@e2m-customer-portal/ui';
import { CircularProgress, Grid, Typography } from '@mui/material';
import { addDays, Interval, isBefore, parseISO, subDays, subSeconds } from 'date-fns';
import { ForecastTarget, Resolution } from 'generated/graphql';
import * as R from 'ramda';
import { ReactElement, Suspense, useEffect, useMemo, useState } from 'react';
import { Matcher } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';
import { getErrorTranslationKey, validatedInput } from 'utils';
import { DayOfWeek } from 'weekstart';

const ValidatedTimeSliceSelector = validatedInput(TimeSliceSelector, 'selected');
const ValidatedWeekdaySelector = validatedInput(WeekdaySelector, 'selected');

const useStyles = makeStyles()(() => ({
  loading: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '200px',
  },
}));

function getHourSelectionValidation(message: string): Validation<HourSelection | undefined> {
  return validation(message, (value) => value != null && R.range(0, 24).some((hour) => value[hour]));
}

function getWeekdaySelectionValidation(message: string): Validation<WeekdaySelection | undefined> {
  return validation(message, (value) => value != null && R.range(0, 7).some((day) => value[day as DayOfWeek]));
}

interface ForecastAssistantProps {
  open: boolean;
  device: Device;
  marketLocation: MarketLocation;
  interval: Interval;
  onClose: () => void;
}

export function ForecastAssistant({
  open,
  marketLocation,
  device,
  interval: tableInterval,
  onClose,
}: Readonly<ForecastAssistantProps>): ReactElement<ForecastAssistantProps> {
  const { classes } = useStyles();
  const [t] = useTranslation();
  const locale = useLocale();
  const [inputsVisible, setInputsVisible] = useState<HTMLSpanElement | null>(null);
  const { forecastMaxDaysLimit } = useEnvSettings();

  const hourSelectionRules = useMemo(
    () => [getHourSelectionValidation(t('FORECAST_ASSISTANT_ERROR_SELECT_TIME_SLICES'))],
    [t],
  );
  const weekdaySelectionRules = useMemo(
    () => [getWeekdaySelectionValidation(t('FORECAST_ASSISTANT_ERROR_SELECT_WEEKDAYS'))],
    [t],
  );

  const weekStartsOn = useStartOfWeek();

  const [
    { validationMode, interval, hours, weekdays, disabledWeekdays, fields, error },
    { setOpen, setInterval, setHours, setWeekdays, setFields, setLimits, showValidationErrors, setError, clearError },
  ] = useAssistantState(open, device, isControlEnergy(marketLocation.technologies));

  // Filtered for CUPO-9918
  const allowedFields = fields.filter(
    (field) =>
      !isBattery(marketLocation.technologies) ||
      ![InputFieldType.AFRR_NEGATIVE, InputFieldType.AFRR_POSITIVE].includes(field.type),
  );

  const [isValid, setIsValid] = useState(false);
  const { update, isLoading } = useUpdateMutation(
    device?.id,
    interval,
    isControlEnergy(marketLocation.technologies) ? Resolution.FOUR_HOURS : Resolution.ONE_HOUR,
    hours,
    weekdays,
    allowedFields,
    tableInterval,
  );

  // Only hand over the device ID when the assistant is actually open to avoid sending useless queries
  const { loading, data: limits } = useLimits(marketLocation.id, open ? device.id : undefined, interval, setLimits);

  useEffect(() => {
    setOpen(open);
  }, [setOpen, open]);

  const handleSave = (): void => {
    showValidationErrors();
    if (isValid) {
      clearError();
      update()
        .then(() => onClose())
        .catch(setError);
    }
  };

  const vppTarget = marketLocation.forecastProperties?.find((prop) => prop.target === ForecastTarget.VPP);
  const optimizationStart = vppTarget ? parseISO(vppTarget.validFrom) : undefined;

  const marketingEnd = marketLocation.marketingEnd != null && subSeconds(marketLocation.marketingEnd, 1);

  const defaultIntervalStart =
    optimizationStart && !isBefore(optimizationStart, interval.end) ? interval.start : getMinDate(device.deadlines);

  const rangeAfterOptimization =
    optimizationStart && !isBefore(getMinDate(device.deadlines), optimizationStart) ? undefined : interval;

  const defaultIntervalEnd =
    optimizationStart && isBefore(optimizationStart, interval.end) ? optimizationStart : interval.end;

  // Various clamps to put the interval in a valid timeframe
  const preferredInterval =
    optimizationStart && isBefore(getMinDate(device.deadlines), optimizationStart)
      ? { start: defaultIntervalStart, end: defaultIntervalEnd }
      : rangeAfterOptimization;
  const intervalRespectingMarketingEnd =
    marketLocation.marketingEnd && preferredInterval && isBefore(marketLocation.marketingEnd, preferredInterval.end)
      ? { start: subDays(marketLocation.marketingEnd, 7), end: marketLocation.marketingEnd }
      : preferredInterval;
  const intervalRespectingMarketingPeriod =
    marketLocation.marketingStart &&
    preferredInterval &&
    isBefore(preferredInterval.start, marketLocation.marketingStart)
      ? { start: marketLocation.marketingStart, end: addDays(marketLocation.marketingStart, 7) }
      : intervalRespectingMarketingEnd;

  return (
    <Drawer open={open} onClose={onClose} testId={`Assistant-${device.name}`}>
      <DrawerHeader cancelLabel={t('SHARED_CANCEL')} noDivider>
        {isControlEnergy(marketLocation.technologies) ? (
          <Typography variant="h3">{t('FORECAST_ASSISTANT_CONTROL_ENERGY_TITLE')}</Typography>
        ) : (
          <Typography variant="h3">{t('FORECAST_ASSISTANT_TITLE')}</Typography>
        )}
        <Typography variant="body2">{marketLocation.externalName ?? marketLocation.name}</Typography>
        <div className="-ml-3.5 mt-4">
          <MasterDataItem
            header={device.externalName ? device.name : t('FORECAST_ASSISTANT_TECHNICAL_UNIT')}
            body={device.externalName || device.name}
          />
        </div>
      </DrawerHeader>

      <DrawerContent className="pt-2" unknownErrorLabel={t('SHARED_ERROR_UNKNOWN')}>
        <ValidatedContainer validate={validationMode} onValidated={setIsValid}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1">{t('FORECAST_ASSISTANT_SELECT_DATE_RANGE')}</Typography>
            </Grid>

            <Grid item xs={12}>
              <DateTimePicker
                mode="range"
                disabled={isLoading}
                label={t('FORECAST_ASSISTANT_DATE_RANGE')}
                pickerProps={{
                  dateOnly: true,
                  disabled: [
                    {
                      before:
                        marketLocation.marketingStart &&
                        isBefore(getMinDate(device.deadlines), marketLocation.marketingStart)
                          ? marketLocation.marketingStart
                          : getMinDate(device.deadlines),
                    },
                    { after: (optimizationStart && subSeconds(optimizationStart, 2)) ?? marketingEnd } as Matcher,
                  ],

                  fixedWeeks: true,
                  numberOfMonths: 2,
                  showWeekNumber: true,
                  locale,
                  startOfWeek: weekStartsOn,
                  rangeMaxDays: forecastMaxDaysLimit,
                  labels: {
                    labelNext: () => t('DATE_TIME_PICKER_NEXT_MONTH'),
                    labelPrevious: () => t('DATE_TIME_PICKER_PREVIOUS_MONTH'),
                  },
                }}
                onSelect={setInterval}
                required
                range={intervalRespectingMarketingPeriod}
              />
            </Grid>

            <Grid item xs={12}>
              <ValidatedTimeSliceSelector
                name="time-slices"
                heading={t('FORECAST_ASSISTANT_TIME_SLICES')}
                hourStep={isControlEnergy(marketLocation.technologies) ? 4 : 1}
                disabled={isLoading}
                selected={hours}
                onChange={setHours}
                rules={hourSelectionRules}
              />
            </Grid>

            <Grid item xs={12}>
              <ValidatedWeekdaySelector
                name="weekdays"
                heading={t('FORECAST_ASSISTANT_WEEKDAYS')}
                disabled={isLoading || disabledWeekdays}
                selected={weekdays}
                onChange={setWeekdays}
                rules={weekdaySelectionRules}
              />
            </Grid>

            <Suspense
              fallback={
                <Grid item xs={12} className={classes.loading}>
                  <CircularProgress />
                </Grid>
              }
            >
              {loading ? (
                <Grid item xs={12} className={classes.loading}>
                  <CircularProgress />
                </Grid>
              ) : (
                <>
                  <span ref={setInputsVisible} />
                  {device && limits && (
                    <ForecastAssistantInputs
                      device={device}
                      templateLimits={limits.template}
                      fieldLimits={limits.fields}
                      disabled={isLoading}
                      interval={interval}
                      fields={allowedFields}
                      isControlEnergy={isControlEnergy(marketLocation.technologies)}
                      onChange={setFields}
                    />
                  )}
                </>
              )}
            </Suspense>
          </Grid>
        </ValidatedContainer>
      </DrawerContent>

      <DrawerFooter testId="forecast-assistant-footer">
        <DrawerButtons
          onCancel={onClose}
          onSave={handleSave}
          isLoading={isLoading}
          disableSave={!device || !inputsVisible || isLoading}
          cancelLabel={t('SHARED_CANCEL')}
          saveLabel={t('SHARED_SUBMIT')}
          error={error ? t('FORECAST_ASSISTANT_ERROR_SAVE', { error: t(getErrorTranslationKey(error)) }) : undefined}
        />
      </DrawerFooter>
    </Drawer>
  );
}
